import { memo, useCallback, useMemo, useState} from 'react';
import { useDispatch } from 'react-redux';
import { Grid } from '@mui/material';
import { KeyedMutator } from 'swr';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { IOption } from '@shared/interfaces';
import { useRefreshComments } from '@shared/hooks/useRefreshComments';
import { SaveButton } from '@shared/inputs/Buttons';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { IDropdownUsersProps } from '@modules/ContactManager/interfaces';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { handleEditDealUser } from '../../queries';
import { DealUserTypeEnum, DealUserTypeLabel, IDeal, IDealUser, orderedDealDropdown } from '../../interfaces';

const ROLE_USER = 'sdrUserSelectOptions';

interface IDealUserProps {
  dealId: string;
  dealData?: IDeal;
  userSelectOptions: IDropdownUsersProps | IOption[];
  mutateDeal: KeyedMutator<IDeal>;
}

type InitStateType = {
  label: string;
  dealUserType: DealUserTypeEnum;
  userId: string | null;
  options: IOption[];
  defaultValue?: IOption;
};

export const isSpecificDropdownUsersProps = (value: IDropdownUsersProps | IOption[] | null): value is IDropdownUsersProps => {
  if (!value) return false;
  return typeof value === 'object' && ROLE_USER in value;
};

export const getOptions = (dealUserType: DealUserTypeEnum, userSelectOptions: IDropdownUsersProps | IOption[] ): IOption[] => {
  if (isSpecificDropdownUsersProps(userSelectOptions)) {
    switch (dealUserType) {
      case DealUserTypeEnum.SDR:
        return userSelectOptions.sdrUserSelectOptions;
      case DealUserTypeEnum.Title:
        return userSelectOptions.titlesUsersUserSelectOptions;
      case DealUserTypeEnum.LCA:
        return userSelectOptions.lcaUserSelectOptions;
      case DealUserTypeEnum.Bank:
        return userSelectOptions.bankUserSelectOptions;
      case DealUserTypeEnum.Lp:
        return userSelectOptions.lpUsersUserSelectOptions;
      case DealUserTypeEnum.Account:
        return userSelectOptions.accountingUsersUserSelectOptions;
      default:
        return userSelectOptions.lcUserSelectOptions;
    }
  } else return userSelectOptions;
};

export const DealUsers = memo(({ dealId, dealData, userSelectOptions, mutateDeal}: IDealUserProps) => {
  const { userHasRoles, isAdmin } = useUserAccount();
  const [selectedValues, setSelectedValues] = useState<Record<DealUserTypeEnum, IDealUser> | Record<string, never>>({});
  const mutateDealComment = useRefreshComments(dealId);
  const dispatch = useDispatch();

  const data: InitStateType[] = useMemo(
    () => {
      const mappedSelectedUserIds: Record<DealUserTypeEnum, IDealUser> | Record<string, never> = (dealData?.dealUsersResponses ?? []).reduce((acc, cur) => {
        if (cur.dealUserType) {
          return {
            ...acc,
            [cur.dealUserType]: cur,
          };
        }
        return acc;
      }, {});
      setSelectedValues(mappedSelectedUserIds);
      const mappedDropdownData = orderedDealDropdown.map((dealUserType) => {
       return {
          dealUserType,
          label: DealUserTypeLabel[dealUserType],
          userId: mappedSelectedUserIds[dealUserType],
          options: getOptions(dealUserType, userSelectOptions),
        };
      });
      return mappedDropdownData;

    },
    [dealData?.dealUsersResponses, userSelectOptions]);

  const canSave = isAdmin || userHasRoles([Roles.DEAL_USER_ASSIGNMENT]);

  const handleSubmitChangeButton = useCallback(
    async () => {
      await handleEditDealUser(dealId, Object.values(selectedValues));
      mutateDeal();
      mutateDealComment();
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
    }, 
    [dealId, selectedValues]
  );

  const onChange = useCallback(
    (key: DealUserTypeEnum, selectedUser: IOption) => {
      setSelectedValues(prevValues => {
        if (!selectedUser) {
          delete prevValues[key];

          return {
            ...prevValues,
          };
        } 

        return {
          ...prevValues,
          [key]: {
            dealUserType: key,
            userId: selectedUser.value,
            userName: selectedUser.label
          }
        };
      });
    },
    []
  );

  return (
    <>
      {data?.map((item) => {
        const options = item?.options || [];
        const selelctedVal = selectedValues[item.dealUserType];
        const isOptionExist = options.find((opt) => opt.value === selelctedVal?.userId);
        const getOptions = isOptionExist ? options : [...options, { label: selelctedVal?.userName ?? '', value: selelctedVal?.userId ?? '' }];
        return (
          options.length > 0 && (
            <Grid
              key={item.dealUserType}
              container
              alignItems="center"
            >
              <Grid item xs={4}>
                {item.label}
              </Grid>

              <Grid item xs>
                <SelectAutocomplete
                  dense
                  removeExtraPadding
                  isFitContentWidth
                  value={selelctedVal?.userId}
                  placeholder="Select User"
                  options={getOptions}
                  onChange={(_value, user: IOption<string>) => onChange(item.dealUserType, user)}
                />
              </Grid>
            </Grid>
          )
        );
      })}

      <SaveButton
        width="100%"
        variant="contained"
        disabled={!canSave}
        onClick={handleSubmitChangeButton}
      />
    </>
  );
});


